import React, { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { apiRequest } from "../../apiServices/allApi";

const PERMISSION_GROUPS = [
  {
    title: "1. Dashboard & Earnings",
    items: [
      { key: "dashboard", label: "Dashboard" },
      { key: "earnings", label: "Earnings" },
    ],
  },
  {
    title: "2. Admin Manager",
    items: [
      { key: "allAdmins", label: "All Admins" },
      { key: "newAdmin", label: "New Admin" },
      { key: "adminKyc", label: "Admin KYC" },
      { key: "sitesettings", label: "Site Settings" },
      { key: "Gatewaysettings", label: "Gateway Settings" },
      { key: "addBankDetail", label: "Add Bank Detail" },
      { key: "viewBankDetail", label: "View Bank Detail" },
      { key: "allAgent", label: "All Agents" },
    ],
  },
  {
    title: "3. User Manager",
    items: [
      { key: "allUsers", label: "All Users" },
      { key: "pendingKyc", label: "Pending KYC" },
      { key: "completedKyc", label: "Completed KYC" },
      { key: "rejectKyc", label: "Reject KYC" },
      { key: "pushNotification", label: "Notification" },
      { key: "legalData", label: "Legal Data" },
    ],
  },
  {
    title: "4. Challenge Manager",
    items: [
      { key: "conflictChallenges", label: "Conflict Challenges" },
      { key: "pendingChallenges", label: "Pending Challenges" },
      { key: "allChallenges", label: "All Challenges" },
      { key: "completedChallenges", label: "Completed Challenges" },
      { key: "cancelledChallenges", label: "Cancelled Challenges" },
      { key: "runningChallenges", label: "Running Challenges" },
      { key: "newChallenge", label: "New Challenge" },
    ],
  },
  {
    title: "5. Transaction Manager",
    items: [
      { key: "allWithdrawlRequests", label: "All Withdrawal Requests" },
      { key: "depositManual", label: "Deposit Manual" },
      { key: "depositHistory", label: "Deposit History" },
      { key: "bonus", label: "Bonus" },
      { key: "penalty", label: "Penalty" },
    ],
  },
  {
    title: "6. Reports",
    items: [
      { key: "bonusReport", label: "Bonus Report" },
      { key: "depositReport", label: "Deposit Report" },
      { key: "penaltyReport", label: "Penalty Report" },
      { key: "withdrawlReport", label: "Withdrawal Report" },
      { key: "blockUser", label: "Block User" },
    ],
  },
];

function Permissions() {
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const [savingKey, setSavingKey] = useState(null);
  const [adminUser, setAdminUser] = useState(null);
  const [permissions, setPermissions] = useState({});

  const access_token = localStorage.getItem("token");
  const headers = {
    Authorization: `Bearer ${access_token}`,
  };

  const fetchPermissions = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiRequest(
        `admin/admin-permission/${id}`,
        "GET",
        null,
        headers,
      );
      if (res && res.data) {
        const permDoc = res.data.data || res.data;
        setPermissions(permDoc || {});
        if (permDoc.adminId) {
          setAdminUser(permDoc.adminId);
        }
      }
    } catch (e) {
      if (e.response?.status === 401) {
        localStorage.removeItem("token");
      }
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchPermissions();
  }, [fetchPermissions]);

  const togglePermission = async (key, currentValue) => {
    const newValue = !currentValue;
    setSavingKey(key);
    // Optimistic update
    setPermissions((prev) => ({
      ...prev,
      [key]: newValue,
    }));

    try {
      await apiRequest(
        "admin/admin-permission/assign",
        "POST",
        {
          adminId: id,
          permissions: {
            ...permissions,
            [key]: newValue,
          },
        },
        headers,
      );
    } catch (e) {
      // Revert on error
      setPermissions((prev) => ({
        ...prev,
        [key]: currentValue,
      }));
    } finally {
      setSavingKey(null);
    }
  };

  return loading ? (
    null
  ) : (
    <div className="row">
      <div className="col-12 grid-margin">
        <div className="card">
          <div className="card-body">
            <div className="d-flex justify-content-between align-items-center mb-4">
              <div>
                <h4 className="card-title mb-1">Permissions Management</h4>
                {adminUser && (
                  <p className="text-muted mb-0">
                    User:{" "}
                    <strong className="text-primary">{adminUser.Name}</strong> |
                    Phone: <strong>{adminUser.Phone}</strong> | Type:{" "}
                    <span className="badge badge-outline-info">
                      {adminUser.user_type}
                    </span>
                  </p>
                )}
              </div>
            </div>

            {/* Permission Groups */}
            <div className="row">
              {PERMISSION_GROUPS.map((group, groupIdx) => (
                <div className="col-md-6 mb-4" key={groupIdx}>
                  <div className="card h-100">
                    <div
                      className="card-header py-3"
                      style={{
                        backgroundColor: "#f8fafc",
                        borderBottom: "1px solid #e2e8f0",
                      }}
                    >
                      <h5
                        className="mb-0 text-dark font-weight-bold"
                        style={{ fontSize: "15px" }}
                      >
                        {group.title}
                      </h5>
                    </div>
                    <ul className="list-group list-group-flush">
                      {group.items.map((item) => {
                        const isChecked = Boolean(permissions[item.key]);
                        return (
                          <li
                            key={item.key}
                            className="list-group-item d-flex justify-content-between align-items-center bg-transparent"
                          >
                            <span>{item.label}</span>
                            <div className="custom-control custom-switch">
                              <input
                                type="checkbox"
                                className="custom-control-input"
                                id={`perm-${item.key}`}
                                checked={isChecked}
                                disabled={savingKey === item.key}
                                onChange={() =>
                                  togglePermission(item.key, isChecked)
                                }
                              />
                              <label
                                className="custom-control-label"
                                htmlFor={`perm-${item.key}`}
                              >
                                {isChecked ? "Allowed" : "Disabled"}
                              </label>
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Permissions;
